import Link from "next/link";
import type { ReactNode } from "react";

import { LogoOI } from "@/components/common/LogoOI";
import { ETICHETTA, RIMANDO, TITOLO_LINK } from "@/components/home/stile";

/** L'intestazione di una fascia della prima pagina nel disegno del
 *  15/10/2026 (`.oi-sez`): il segno «OI» nel mare col triangolo nel rame,
 *  il titolo in serif col punto finale, accanto una riga che dice di che
 *  cosa si tratta e a destra il rimando, sopra un filetto di due pixel
 *  nell'inchiostro. Sul telefono il rimando va a capo.
 *
 *  Il disegno scrive il titolo a 34 pixel: qui è `text-fascia` (24-32),
 *  il gradino della scala più vicino. Il titolo è un h2 con un id, così
 *  la fascia (`<section aria-labelledby>`) ha un nome. */
export function TestaSezione({
  id,
  titolo,
  sottotitolo,
  href,
  rimando,
  extra,
}: {
  id: string;
  titolo: string;
  /** Se c'è, il titolo stesso porta lì (la fascia dei casi porta al Rullo
   *  filtrato). Il segno resta fuori dal collegamento. */
  href?: string;
  sottotitolo?: string;
  rimando?: { href: string; etichetta: string };
  /** Qualcosa da mettere al posto del sottotitolo (l'indicatore «In
   *  aggiornamento» del flusso). */
  extra?: ReactNode;
}) {
  return (
    <div className="mb-heading flex flex-wrap items-baseline justify-between gap-x-6 gap-y-3 border-b-2 border-ink pb-3">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-5 gap-y-2">
        <h2 id={id} className="m-0 font-serif text-fascia font-normal leading-[1.15] text-ink">
          <SegnoTitolo />
          {href ? (
            <Link href={href} className={TITOLO_LINK}>
              {titolo}
            </Link>
          ) : (
            titolo
          )}
        </h2>
        {sottotitolo ? <span className={ETICHETTA}>{sottotitolo}</span> : null}
        {extra}
      </div>
      {rimando ? (
        <Link href={rimando.href} className={RIMANDO}>
          {rimando.etichetta} <span aria-hidden>↗</span>
        </Link>
      ) : null}
    </div>
  );
}

/** Il segno davanti a un titolo serif: alto quanto le minuscole, nel
 *  mare, col triangolo nel rame. Misure del disegno (0,8em × 0,64em,
 *  0,36em di stacco). */
export function SegnoTitolo() {
  return (
    <LogoOI
      variant="segno"
      decorative
      accento="fill-segnale"
      className="mr-[0.36em] inline-block h-[0.64em] w-auto align-baseline text-mare"
    />
  );
}
